import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BatchConfiguration, BatchConfigurationPayload } from '../types/BatchConfigType';
import { useSaveBatchConfiguration } from './useSaveBatchConfiguration';
import { toast } from 'react-toastify';

export function useBatchConfigurationForm(initialFormData: BatchConfiguration): {
  formData: BatchConfiguration;
  isRecurrenceDialogOpen: boolean;
  setIsRecurrenceDialogOpen:  React.Dispatch<React.SetStateAction<boolean>>;
  getUnselectedStatusOptions: (options: Array<{ label: string; value: string }>) => Array<{ label: string; value: string }>;
  getAvailableActionOptionsForCondition: (currentIndex: number, options: Array<{ label: string; value: string }>) => Array<{ label: string; value: string }>;
  updateField: (
    field: keyof BatchConfiguration,
    value: BatchConfiguration[keyof BatchConfiguration]
  ) => void;
  errors: Partial<Record<keyof BatchConfiguration, any>>;
  onSave: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel: () => void;
  isSavingBatchConfiguration: boolean;
  saveBatchConfigurationError: unknown;
  savedBatchConfigurationData: unknown;
  handleAddNewStatusCondition: () => void,
  handleDeleteStatusCondition: (index: number) => void,
  handleConditionChange: (index: number, value: any, key: string) => void,
  handleRecurrenceSubmit: (data: any) => void,
} {
  const [formData, setFormState] = useState<BatchConfiguration>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof BatchConfiguration, any>>>({});
  const [isRecurrenceDialogOpen, setIsRecurrenceDialogOpen] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    saveBatchConfigurationMutation,
    isSavingBatchConfiguration,
    saveBatchConfigurationError,
    savedBatchConfigurationData,
  } = useSaveBatchConfiguration();



  const validateField = useCallback(
    (field: keyof BatchConfiguration, value: BatchConfiguration[typeof field]): boolean => {
      let errorMessage = '';
      if (field === 'name' && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errorMessage = t('batchConfig.nameRequired', 'Name is required');
      }
      if (field === 'purpose' && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errorMessage = t('batchConfig.purposeRequired', 'Purpose is required');
      }
      if (field === 'batchType' && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errorMessage = t('batchConfig.batchTypeRequired', 'Batch Type is required');
      }
      if (field === 'detectionType' && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errorMessage = t('batchConfig.detectionTypeRequired', 'Detection Type is required');
      }
      if (field === 'selectedEvent' && formData?.detectionType === 'EVENT_COMPLETION' && (!value || value?.eventComponentId === '')) {
        errorMessage = t('batchConfig.eventSelectionRequiredCap', 'Event Selection is Required');
      }
      // if (field === 'startBatchAction'  && formData?.detectionType === 'EVENT_COMPLETION' && (!value || (typeof value === 'string' && value.trim() === ''))) {
      //   errorMessage = 'Start Batch Action is required';
      // }
      if (field === 'selectedEquipments' && (!value || (Array.isArray(value) && value.length === 0))) {
        errorMessage = t('batchConfig.equipmentSelectionRequired', 'At least one Equipment Selection is required');
      }
      if (field === 'trackingSensors' && (!value || (Array.isArray(value) && value.length === 0))) {
        errorMessage = t('batchConfig.trackingSensorRequired', 'At least one tracking sensor is required');
      }
      if (field === 'timeCycle' && formData.detectionType === 'TIME_CYCLE' && (!value || !value)) {
        errorMessage = t('batchConfig.timeCycleDetectionRequired', 'Time Cycle is required with this Detection Type');
      }

      if (field === 'waterTreatment' && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errorMessage = t('batchConfig.batchTypeRequired', 'Batch Type is required');
      }
      
      

    
      setErrors(prev => ({ ...prev, [field]: errorMessage }));
      return errorMessage === '';
    },
    [formData.detectionType, t]
  );

  const updateField = useCallback(
    (field: keyof BatchConfiguration, value: BatchConfiguration[typeof field]) => {
      setFormState(prev => ({ ...prev, [field]: value }));
      validateField(field, value);
    },
    [validateField]
  );

  
  const validateFormData = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof BatchConfiguration, any>> = {};
    if (!formData.name || formData.name.trim() === '') newErrors.name = t('batchConfig.nameRequired', 'Name is required');
    if (!formData.purpose || formData.purpose.trim() === '') newErrors.purpose = t('batchConfig.purposeRequired', 'Purpose is required');
    if (!formData.batchType || formData.batchType.trim() === '') newErrors.batchType = t('batchConfig.batchTypeRequired', 'Batch Type is required');
    if (!formData.detectionType || formData.detectionType.trim() === '') newErrors.detectionType = t('batchConfig.detectionTypeRequired', 'Detection Type is required');
    if (!formData.waterTreatment?.unit || formData.waterTreatment.unit.trim() === '') {
      newErrors.waterTreatment = {
        ...newErrors.waterTreatment,
        unit: t('batchConfig.waterTreatmentUnitRequired', 'Water Treatment Unit is required'),
      };
    };
    if (!formData.waterTreatment?.value || formData.waterTreatment.value === 0) {
      newErrors.waterTreatment = {
        ...newErrors.waterTreatment,
        value: t('batchConfig.waterTreatmentValueRequired', 'Water Treatment Value is required'),
      };
    };
    if (!formData.selectedEquipments || formData.selectedEquipments.length === 0) {
      newErrors.selectedEquipments = t('batchConfig.equipmentSelectionRequired', 'At least one Equipment Selection is required');
    }
    if (!formData.trackingSensors || formData.trackingSensors.length === 0) {
      newErrors.trackingSensors = t('batchConfig.trackingSensorRequired', 'At least one tracking sensor is required');
    }
    if (formData.detectionType === 'EVENT_COMPLETION') {
      if(!formData?.selectedEvent || !formData?.selectedEvent?.eventComponentId){
      newErrors.selectedEvent = t('batchConfig.eventSelectionDetectionRequired', 'Event Selection is required with this Detection Type');
    }
      if(!formData?.startBatchAction){
        newErrors.startBatchAction = t('batchConfig.batchActionRequired', 'Batch action is required');
    }
    }
    if (formData?.selectedEvent && formData?.selectedEvent?.eventComponentId && !formData?.startBatchAction) {
      newErrors.startBatchAction = t('batchConfig.batchActionRequired', 'Batch action is required');
    }
    if (!formData.detectionType || formData.detectionType === 'TIME_CYCLE') {
      if(!formData?.timeCycle || !formData?.timeCycle)
      newErrors.timeCycle = t('batchConfig.timeCycleDetectionRequired', 'Time Cycle is required with this Detection Type');
    }
    if (!formData.attachedConditions || formData.attachedConditions.length === 0) {
      newErrors.attachedConditions = t('batchConfig.attachedConditionRequired', 'At least one attached condition is required');
    } else {
      const perConditionErrors: Array<{ status?: string; condition?: string; action?: string }> = [];
      (formData.attachedConditions as unknown as any[]).forEach((cond, idx) => {
        const entry: { status?: string; condition?: string; action?: string } = {};
        const status = cond?.status;
        const eventSelection = cond?.event;
        const action = cond?.action;
        if (!status || (typeof status === 'string' && status.trim() === '')) {
          entry.status = t('batchConfig.statusRequired', 'Status is required');
        }
        if (!eventSelection || (Array.isArray(eventSelection) && eventSelection.length === 0)) {
          entry.condition = t('batchConfig.eventSelectionRequired', 'Event selection is required');
        }
        if (!action || (typeof action === 'string' && action.trim() === '')) {
          entry.action = t('batchConfig.actionRequired', 'Action is required');
        }
        perConditionErrors[idx] = entry;
      });
      if (perConditionErrors.some(e => e.status || e.condition)) {
        newErrors.attachedConditions = perConditionErrors as unknown as any;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);


  
  const handleAddNewStatusCondition = () => {
    const existing = (formData.attachedConditions as unknown as any[]) || [];
    const newCondition = { status: '', event: [], action: '' };
    updateField('attachedConditions', [...existing, newCondition] as unknown as any);
  };

  const handleDeleteStatusCondition = (index: number) => {
    const newConditions = (formData.attachedConditions || []).filter((_, i) => i !== index);
    updateField('attachedConditions', newConditions);
  };

  const handleConditionChange = (index: number, value: any, dataKey: string) => {
    const newConditions = ([...(formData.attachedConditions as unknown as any[])]);
    newConditions[index] = { ...(newConditions[index] || {}), [dataKey]: value };
    updateField('attachedConditions', newConditions as unknown as any);
  };

  const handleRecurrenceSubmit = useCallback((data: any) => {
    const processedData = {
      ...data,
      startDate: data?.startDate ? new Date(data.startDate).getTime() : data.startDate,
    };
    updateField('timeCycle', processedData);
    setIsRecurrenceDialogOpen(false);
  }, [updateField]);


  const getUnselectedStatusOptions = useCallback(
    (options: Array<{ label: string; value: string }>): Array<{ label: string; value: string }> => {
      const selectedStatuses = new Set(
        ((formData.attachedConditions as unknown as any[]) || [])
          .map((c) => (c && c.status ? String(c.status).toUpperCase() : ''))
          .filter(Boolean)
      );
      return options.filter((opt) => !selectedStatuses.has(String(opt.value).toUpperCase()));
    },
    [formData.attachedConditions]
  );

  const getAvailableActionOptionsForCondition = useCallback(
    (currentIndex: number, options: Array<{ label: string; value: string }>): Array<{ label: string; value: string }> => {
      const conditions = (formData.attachedConditions as unknown as any[]) || [];
      const currentCondition = conditions[currentIndex];
      
      if (currentCondition?.event?.eventComponentId) {
        const currentEventId = currentCondition.event.eventComponentId;
        const usedActionsWithThisEvent = new Set(
          conditions
            .filter((_, idx) => idx !== currentIndex)
            .filter(cond => cond.event?.eventComponentId === currentEventId && cond.action)
            .map(cond => cond.action?.toUpperCase())
            .filter(Boolean)
        );
        
        return options.filter(opt => 
          !usedActionsWithThisEvent.has(opt.value.toUpperCase()) || 
          (currentCondition.action && opt.value.toUpperCase() === currentCondition.action.toUpperCase())
        );
      }
      
      return options;
    },
    [formData.attachedConditions]
  );



  const handleSubmit = useCallback(
    (data: BatchConfigurationPayload) => {
      saveBatchConfigurationMutation(data);
    },
    [saveBatchConfigurationMutation]
  );

  const onSave = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      if (!formData?.attachedConditions || formData.attachedConditions.length === 0) {
        toast.error(t('batchConfig.statusConditionRequired', 'Please add at least one Batch Status Condition'));
        return;
      }
      if ( formData?.detectionType==='TIME_CYCLE' &&  !formData?.timeCycle) {
        toast.error(t('batchConfig.timeCycleRequired', 'Please configure the Time Cycle Rule'));
        return;
      }
      // if (formData?.detectionType==='EVENT_COMPLETION' && !formData?.selectedEvent?.eventComponentId) {
      //   toast.error('Please Select a event');
      //   return;
      // }
      if (!formData?.batchFlow || !Array.isArray(formData.batchFlow.nodes) || formData.batchFlow.nodes.length === 0) {
        toast.error(t('batchConfig.batchFlowRequired', 'Please configure the Batch Flow Properly'));
        return;
      }
      if (!Array.isArray(formData.batchFlow.edges) || formData.batchFlow.edges.length === 0) {
        toast.error(t('batchConfig.batchFlowRequired', 'Please configure the Batch Flow Properly'));
        return;
      }
      const isValid = validateFormData();
      if (isValid) {
        let payload: BatchConfigurationPayload = {
            batchId: formData?.batchId,
            name: formData.name,
            selectedEquipments: (formData?.selectedEquipments || []).map((eq: any) => eq?.equipmentId).filter(Boolean),
            detectionType: formData.detectionType,
            timeCycle: formData?.timeCycle,
            selectedEvent: formData?.selectedEvent?.eventComponentId,
            startBatchAction: formData?.detectionType === 'EVENT_COMPLETION' ? formData?.startBatchAction : undefined,
            trackingSensors: (formData?.trackingSensors || []).map((s: any) => (typeof s === 'string' ? s : s?.sensorId)).filter(Boolean),
            batchFlow: formData.batchFlow || { nodes: [], edges: [] },
            // chemicalUsage: (formData?.chemicalUsage || []).map((c: any) => (typeof c === 'string' ? c : c?.itemId)).filter(Boolean),
            chemicalUsage: formData?.chemicalUsage,
            waterTreatment: formData.waterTreatment || { value: '', unit: '' },
            batchType: formData.batchType,
            purpose: formData.purpose,
            assetId: formData.assetId,
            attachedConditions: (formData?.attachedConditions as unknown as any[] | undefined)?.map((ac: any) => ({
              status: ac?.status ?? '',
              event: Array.isArray(ac?.event)
                ? (ac.event[0]?.eventComponentId || ac.event[0] || '')
                : (ac?.event?.eventComponentId || ac?.event || ''),
              action: ac?.action ?? '',
            })) || [],
        };
        handleSubmit(payload);
      }
    },
    [formData, validateFormData, handleSubmit, t]
  );

  const onCancel = useCallback(() => {
    navigate('../');
  }, [navigate]);

  return {
    formData,
    isRecurrenceDialogOpen,
    setIsRecurrenceDialogOpen,
    getUnselectedStatusOptions,
    getAvailableActionOptionsForCondition,
    updateField,
    errors,
    onSave,
    onCancel,
    isSavingBatchConfiguration,
    saveBatchConfigurationError,
    savedBatchConfigurationData,
    handleAddNewStatusCondition,
    handleDeleteStatusCondition,
    handleConditionChange,
    handleRecurrenceSubmit,
  };
}
